import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dropdown, Flex, Input, List, MenuProps, Skeleton, Typography } from 'antd';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useParams } from 'react-router-dom';
import { getTopics } from 'src/apis/helpdesk/topic.api';
import MoreIcon from 'src/assets/icons/more2.png';
import Icon from 'src/components/v2/Icon';
import { useBasePath } from 'src/hooks/useBasePath';
import { useChatStore } from 'src/stores/chatStore';
import { ITopic, RoomChatState } from 'src/types/helpdesk/topic.type';
import { convertMessionInput, getLastname } from 'src/utils/utils';
import { useChatFileService } from '../hook/useChatFileService';
import './Topic.scss';
import TopicLastMessageTime from './TopicLastMessageTime';
import { AppContext } from 'src/contexts/app.context';
const { Text, Title } = Typography;

export default function Topics() {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const [searchResult, setSearchResult] = useState<RoomChatState[]>([]);
  const { userInfo } = useContext(AppContext);
  //store
  const { resetNewMessage } = useChatStore();
  const { clearAllIndexedDB } = useChatFileService();

  // Store
  const topicParams = useChatStore((state) => state.topicParams);
  const updateTopicParams = useChatStore((state) => state.updateTopicParams);
  const setRooms = useChatStore((state) => state.setRooms);
  const addRooms = useChatStore((state) => state.addRooms);
  const rooms = useChatStore((state) => state.rooms);
  const resetStore = useChatStore((s) => s.resetStore);

  const topicList = Object.values(rooms);

  const { id } = useParams();

  const topicsQuery = useQuery({
    queryKey: ['topics', topicParams.page, topicParams.pageSize, topicParams.isActive],
    queryFn: () => getTopics(topicParams),
    staleTime: 30 * 1000, // 30 seconds cache
    refetchOnWindowFocus: false
  });
  const hasMore = (topicsQuery?.data?.data?.pageNumber || 0) < (topicsQuery?.data?.data?.totalPages || 0);

  useEffect(() => {
    if (topicsQuery?.data?.data?.results) {
      const { results, pageNumber, totalPages } = topicsQuery.data.data;

      // Sync full data to store
      if (pageNumber <= 1) {
        setRooms(results as ITopic[]); // ✅ Full data with lastestTopicDetail & countUnReadMessages
      } else {
        addRooms(results as ITopic[]); // ✅ Append for pagination
      }

      // Update pagination in store
      useChatStore.setState({
        topicsPagination: {
          currentPage: pageNumber || 1,
          totalPages: totalPages || 1,
          hasMore: (pageNumber || 1) < (totalPages || 1)
        }
      });
    }
  }, [topicsQuery?.data, setRooms, addRooms]);

  const items = function (): MenuProps['items'] {
    const items = [
      {
        key: '0',
        onClick: () => {
          updateTopicParams((prev) => ({ ...prev, page: 1, isActive: true }));
        },
        label: 'Đang hoạt động',
        icon: topicParams.isActive ? <CheckOutlined /> : <CheckOutlined style={{ color: 'transparent' }} />
      },
      {
        key: '1',
        onClick: () => {
          updateTopicParams((prev) => ({ ...prev, page: 1, isActive: false }));
        },
        label: 'Đã đóng',
        icon: !topicParams.isActive ? <CheckOutlined /> : <CheckOutlined style={{ color: 'transparent' }} />
      }
    ];
    return items;
  };

  const handleLoadMore = () => {
    if (topicsQuery.isFetching || !hasMore) return;

    // ✅ Update params để trigger new query
    updateTopicParams((prev) => ({
      ...prev,
      page: Number(prev.page) + 1
    }));
  };

  const handleSelectTopic = (topicId: string) => {
    if (!topicId || topicId === id) return;

    navigate(`${basePath}/${topicId}`);

    resetNewMessage();
  };

  useEffect(() => {
    const element = document.querySelector('.topic-list');
    if (element) {
      element.addEventListener('click', (event) => {
        event.preventDefault();
      });
    }
  }, []);

  const handleSearchTopic = (event: ChangeEvent<HTMLInputElement>) => {
    const results = topicList.filter((topic) =>
      topic?.topicCode?.toLowerCase().includes(event.target.value.toLowerCase())
    );

    setSearchResult(results);
  };

  return (
    <div className='topics-container'>
      <Flex justify='space-between' align='center' className='topic-list-header'>
        <Title level={5} className='topic-group-title'>
          Ticket
        </Title>
        <Button
          onClick={() => {
            resetStore();
            clearAllIndexedDB();
          }}
        >
          Reset store
        </Button>

        {/* <Tooltip title='Raise ticket'>
          <Button
            className=''
            onClick={() => navigate(`${urlPath.ticketAdd.location}?${ILinkId.ticketTypeId}=${TicketTypeEn.New}`)}
            type='default'
            shape='default'
            size='middle'
            icon={<PlusOutlined />}
          />
        </Tooltip> */}
      </Flex>
      <Flex vertical gap={10}>
        <div className='topics-heading'>
          <div className='topics-search'>
            <Input
              className='topics-search-input'
              prefix={<SearchOutlined className='topics-search-icon' />}
              placeholder='Tìm kiếm'
              allowClear
              onChange={handleSearchTopic}
            />
          </div>
          <Flex className='topics-label' justify='space-between' align='center'>
            <Text>Topics</Text>
            <Dropdown menu={{ items: items() }} trigger={['click']}>
              <Button type='text' icon={<Icon url={MoreIcon} size={16} />} />
            </Dropdown>
          </Flex>
        </div>
        <div id='scrollable-div' className='scrollable-wrapper'>
          <InfiniteScroll
            dataLength={topicList?.length || 0}
            next={handleLoadMore}
            hasMore={hasMore} // ✅ Use từ React Query
            loader={topicsQuery.isFetching ? <Skeleton avatar paragraph={{ rows: 1 }} active /> : null}
            scrollableTarget='scrollable-div'
          >
            <List
              className='topic-list'
              split={false}
              dataSource={
                searchResult.length > 0 ? searchResult : topicList
                // (topicList?.sort(
                //     (a, b) =>
                //       new Date(b?.lastestTopicDetail?.createdDate || b?.createdDate || new Date()).getTime() -
                //       new Date(a?.lastestTopicDetail?.createdDate || a?.createdDate || new Date()).getTime()
                //   ) as ITopic[])
              }
              // loading={topicsQuery.isFetching}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  className={`topic-item ${id === item.id && 'selected-topic'}`}
                  onClick={() => handleSelectTopic(String(item.id))}
                >
                  <List.Item.Meta
                    title={
                      <Badge
                        className='topic-new-message-count'
                        count={item?.countUnReadMessages?.quantity}
                        size='small'
                        offset={[-10, 35]}
                      >
                        <Flex className='topic-item-title' vertical gap={6}>
                          <TopicLastMessageTime
                            createdDate={item?.lastestTopicDetail?.createdDate || item?.createdDate}
                          />
                          <Text className='topic-item-name'>{item?.name || `#${item.topicCode}`}</Text>
                          <Text
                            className={`lastest-message ${
                              (item?.countUnReadMessages?.quantity || 0) > 0 ? 'bold' : 'normal'
                            }`}
                          >
                            {item?.lastestTopicDetail?.senderBy && (
                              <Text>{`${
                                item?.lastestTopicDetail?.senderCode === userInfo?.code
                                  ? 'Bạn'
                                  : getLastname(String(item?.lastestTopicDetail?.senderBy?.name))
                              }: `}</Text>
                            )}

                            {item?.lastestTopicDetail?.filePaths && item?.lastestTopicDetail?.filePaths?.length > 0 && (
                              <Text>{'[File]'}</Text>
                            )}

                            {item?.lastestTopicDetail?.content && (
                              <p
                                className='lastest-message-content'
                                // style={{ display: 'inline' }}
                                dangerouslySetInnerHTML={{
                                  __html: String(
                                    convertMessionInput(String(item?.lastestTopicDetail?.content || ''))?.replace(
                                      /\n\r?/g,
                                      '<br />'
                                    )
                                  )
                                }}
                              />
                            )}
                          </Text>
                        </Flex>
                      </Badge>
                    }
                    description={null}
                  />
                </List.Item>
              )}
            />
          </InfiniteScroll>
        </div>
      </Flex>
    </div>
  );
}
